import { jwtDecode } from "jwt-decode";
import { decrypt } from "./other";
import { CY_SSO_BASE_URL } from "../services/constants";
import { clearAuthCookies } from "./cookieUtils";
import { AUTH_EVENTS, logAuthEventBeforeLeaving } from "./authEventLog";

/**
 * The raw stored session. Returns null when the browser blocks storage
 * (private mode, embedded views) or has none, so callers treat it as signed out.
 */
export const readStoredUser = () => {
  try {
    return window.localStorage.getItem("user");
  } catch {
    return null;
  }
};

export const auth = () => {
  try {
    return !!decrypt(readStoredUser());
  } catch {
    return false;
  }
};

/**
 * Single point for logging out: tells cy-sso to invalidate the session tied
 * to the current access token, then clears local session state. Every call
 * site is immediately followed by a navigation (window.location.replace),
 * which can abort a plain XHR/Axios request mid-flight. `fetch` with
 * keepalive:true is built for exactly this — the browser guarantees the
 * request is sent even though the page is unloading. (navigator.sendBeacon
 * can't be used instead since it doesn't support custom headers, and we
 * need to send the token via Authorization.) Still fire-and-forget:
 * localStorage gets cleared regardless of whether the backend call succeeds.
 *
 * A forced logout (sessionExpired) passes notifyServer: false. cy-sso's
 * api/logout ends the whole session family, even when given an old token, so a
 * tab whose refresh was refused would end the student's live session in every
 * other app and tab. Only a Log out button tells cy-sso.
 */
export const logoutUser = ({ notifyServer = true } = {}) => {
  let accessToken;
  try {
    accessToken = decrypt(readStoredUser())?.ACCESS_TOKEN;
  } catch {
    accessToken = undefined;
  }
  if (notifyServer && accessToken) {
    fetch(`${CY_SSO_BASE_URL}api/logout`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    }).catch(() => {});
  }
  // Clear the shared cross-app cy-sso cookies too, or the boot bootstrap (here
  // and in the standalone Sandbox) would silently re-create the dead session.
  // Runs BEFORE localStorage.clear() so clearAuthCookies can still read the
  // session role and raise the `cy_logged_out` signal for the student guard.
  try {
    clearAuthCookies();
  } catch (_) {
    /* never block logout */
  }
  try {
    localStorage.clear();
  } catch (_) {
    /* storage is blocked: there is nothing to clear */
  }
};

export const getUser = () => {
  try {
    const user = decrypt(readStoredUser());
    if (Object.keys(user).length > 0) {
      const userObj = jwtDecode(user?.ACCESS_TOKEN);
      return {
        studentId: userObj?.userId,
        studentEmail: userObj?.studentEmail,
      };
    }
    return {};
  } catch {
    return {};
  }
};

/**
 * A logout button: logs AUTH_LOGOUT with the button's source (built before
 * the session is cleared; sent with keepalive, so nothing waits for it), logs
 * the user out and goes to the login page.
 */
export const logoutWithLog = (source) => {
  logAuthEventBeforeLeaving(AUTH_EVENTS.LOGOUT, { source });
  logoutUser();
  window.location.replace("/");
};
